import React from 'react';

interface SystemSettings {
  defaultCurrency: 'EGP' | 'USD';
  exchangeRateSource: 'manual' | 'auto';
  dateFormat: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';
  timeFormat: '12h' | '24h';
  defaultLanguage: 'en' | 'ar';
  fiscalYearStart: string;
  enableNotifications: boolean;
  enableEmailAlerts: boolean;
  enableSMSAlerts: boolean;
  autoBackup: boolean;
  backupFrequency: 'daily' | 'weekly' | 'monthly';
}

interface SystemTabProps {
  settings: SystemSettings;
  onUpdate: (patch: Partial<SystemSettings>) => void;
}

const SystemTab: React.FC<SystemTabProps> = ({ settings, onUpdate }) => (
  <div className="space-y-6">
    <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
      <h3 className="font-semibold text-blue-900 mb-2">System Configuration</h3>
      <p className="text-sm text-blue-800">Configure system-wide settings and preferences</p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Default Currency</label>
        <select
          value={settings.defaultCurrency}
          onChange={(e) => onUpdate({ defaultCurrency: e.target.value as 'EGP' | 'USD' })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="EGP">EGP - Egyptian Pound</option>
          <option value="USD">USD - US Dollar</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Exchange Rate Source</label>
        <select
          value={settings.exchangeRateSource}
          onChange={(e) => onUpdate({ exchangeRateSource: e.target.value as 'manual' | 'auto' })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="manual">Manual Entry</option>
          <option value="auto">Automatic Update</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Date Format</label>
        <select
          value={settings.dateFormat}
          onChange={(e) => onUpdate({ dateFormat: e.target.value as any })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="DD/MM/YYYY">DD/MM/YYYY</option>
          <option value="MM/DD/YYYY">MM/DD/YYYY</option>
          <option value="YYYY-MM-DD">YYYY-MM-DD</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Time Format</label>
        <select
          value={settings.timeFormat}
          onChange={(e) => onUpdate({ timeFormat: e.target.value as '12h' | '24h' })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="12h">12 Hour (AM/PM)</option>
          <option value="24h">24 Hour</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Default Language</label>
        <select
          value={settings.defaultLanguage}
          onChange={(e) => onUpdate({ defaultLanguage: e.target.value as 'en' | 'ar' })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="en">English</option>
          <option value="ar">Arabic</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Fiscal Year Start (MM-DD)</label>
        <input
          type="text"
          value={settings.fiscalYearStart}
          onChange={(e) => onUpdate({ fiscalYearStart: e.target.value })}
          placeholder="01-01"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
    </div>

    <div className="space-y-4 pt-4 border-t">
      <h4 className="font-medium text-gray-900">System Preferences</h4>
      <div className="space-y-3">
        <div className="flex items-center">
          <input type="checkbox" checked={settings.enableNotifications}
            onChange={(e) => onUpdate({ enableNotifications: e.target.checked })}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
          <label className="ml-2 block text-sm text-gray-900">Enable Notifications</label>
        </div>
        <div className="flex items-center">
          <input type="checkbox" checked={settings.enableEmailAlerts}
            onChange={(e) => onUpdate({ enableEmailAlerts: e.target.checked })}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
          <label className="ml-2 block text-sm text-gray-900">Enable Email Alerts</label>
        </div>
        <div className="flex items-center">
          <input type="checkbox" checked={settings.enableSMSAlerts}
            onChange={(e) => onUpdate({ enableSMSAlerts: e.target.checked })}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
          <label className="ml-2 block text-sm text-gray-900">Enable SMS Alerts</label>
        </div>
        <div className="flex items-center">
          <input type="checkbox" checked={settings.autoBackup}
            onChange={(e) => onUpdate({ autoBackup: e.target.checked })}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
          <label className="ml-2 block text-sm text-gray-900">Enable Auto Backup</label>
        </div>
      </div>
      {settings.autoBackup && (
        <div className="ml-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Backup Frequency</label>
          <select
            value={settings.backupFrequency}
            onChange={(e) => onUpdate({ backupFrequency: e.target.value as any })}
            className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
      )}
    </div>
  </div>
);

export default SystemTab;
